/**
 * System Health Dashboard — admin-only Type-3 widget surface at /admin/health.
 *
 * Day 99b.1 — replaces the Day-62a Placeholder. Mirrors Dashboard.tsx's shell
 * (h-full overflow-auto → max-w-5xl mx-auto p-5 space-y-4, arbi-card widgets) and
 * the ChartsRow recharts pattern (dark-theme axes + custom tooltip).
 *
 * V1 scope (weigh-in Q7 — only Sentry is reachable today without new substrate):
 *   - Sentry error-rate widget — summary cards (errors 24h / 7d, open issues, last
 *     event) from sentry_events / sentry_fingerprints count queries + an
 *     error-rate-over-time bar chart fed by REAL sentry_events aggregation
 *     (bucketEventsByDay; read shape per ErrorLogViewer — .gte timestamp cutoff).
 *     Debounced Realtime refresh on sentry_fingerprints (a supabase_realtime
 *     member; the receiver UPSERTs it on every event), mirroring Dashboard's
 *     debounced channel.
 *   - Prod-only test-exception button — Sentry.captureException off the wired
 *     main.tsx SDK (enabled only in PROD) → receive-sentry-event EF → sentry_events
 *     → Error Log Viewer + this widget. Disabled in local dev (the SDK's init is
 *     prod-only); the affordance says so.
 *   - Supabase p95 / connection count + Vercel build status are OUT of V1 (need a
 *     pg_stat RPC or a Management/Vercel-API connector + secret that don't exist) —
 *     a muted card documents the deferral per TONE empty-state voice.
 */

import { useState, useEffect, useRef, useCallback, type JSX } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { Bug, AlertTriangle, Activity, Clock, Send, CheckCircle, Server } from 'lucide-react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  bucketEventsByDay,
  isoCutoff,
  totalCount,
  type DayBucket,
} from '../lib/systemHealthAggregation';

const DAY_MS = 86_400_000;
const CHART_DAYS = 7;
// Bound the 7-day events pull — internal error volume is small; this is a guard,
// not a real cap (a week over 10k errors is itself the signal).
const CHART_FETCH_LIMIT = 10_000;

// ── Local types ───────────────────────────────────────────────────────────────

interface ToastState {
  type: 'success' | 'error';
  message: string;
}

interface TooltipEntry {
  color?: string;
  name?: string;
  value?: number;
}

// ── Relative time (inline-replicated per arbi-dev precedent) ────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function fmtCount(n: number | null): string {
  return n === null ? '—' : n.toLocaleString();
}

// ── Toast ───────────────────────────────────────────────────────────────────--

function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastState | null;
  onDismiss: () => void;
}): JSX.Element | null {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const bg =
    toast.type === 'success'
      ? 'bg-success/15 border-success/30 text-success'
      : 'bg-danger/15 border-danger/30 text-danger';
  return (
    <div
      className={clsx(
        'fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm font-medium shadow-lg',
        bg
      )}
    >
      {toast.message}
    </div>
  );
}

// ── Stat card (display-only — MetricCards style, no navigation) ─────────────────

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tone: string;
}): JSX.Element {
  return (
    <div className="arbi-card p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-muted uppercase tracking-wider">{label}</span>
        <Icon size={16} className={tone} />
      </div>
      <div className="text-2xl font-semibold font-mono text-ink-bright">{value}</div>
    </div>
  );
}

// ── Custom dark chart tooltip (mirrors ChartsRow) ───────────────────────────────

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}): JSX.Element | null {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-3 border border-border rounded-md px-3 py-2 shadow-lg text-xs">
      <div className="text-ink-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-ink-secondary">{p.name}:</span>
          <span className="font-mono text-ink-bright">{p.value ?? 0}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────--

export default function SystemHealthDashboard(): JSX.Element {
  useDocumentTitle('System Health Dashboard');
  const isProd = import.meta.env.PROD;

  const [loading, setLoading] = useState<boolean>(true);
  const [errors24h, setErrors24h] = useState<number | null>(null);
  const [errors7d, setErrors7d] = useState<number | null>(null);
  const [openIssues, setOpenIssues] = useState<number | null>(null);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [chartData, setChartData] = useState<DayBucket[]>([]);
  const [toast, setToast] = useState<ToastState | null>(null);

  const axisStyle = {
    fontSize: 11,
    fill: 'var(--ink-muted)',
    fontFamily: "'JetBrains Mono', monospace",
  };

  const fetchAll = useCallback(async (): Promise<void> => {
    const cutoff24 = isoCutoff(DAY_MS);
    const cutoff7 = isoCutoff(7 * DAY_MS);
    const [e24, e7, open, last, recent] = await Promise.all([
      supabase
        .from('sentry_events')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', cutoff24),
      supabase
        .from('sentry_events')
        .select('*', { count: 'exact', head: true })
        .gte('timestamp', cutoff7),
      supabase
        .from('sentry_fingerprints')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open'),
      supabase
        .from('sentry_events')
        .select('timestamp')
        .order('timestamp', { ascending: false })
        .limit(1),
      supabase
        .from('sentry_events')
        .select('timestamp')
        .gte('timestamp', cutoff7)
        .order('timestamp', { ascending: true })
        .limit(CHART_FETCH_LIMIT),
    ]);

    setErrors24h(e24.count ?? 0);
    setErrors7d(e7.count ?? 0);
    setOpenIssues(open.count ?? 0);
    setLastEventAt(last.data?.[0]?.timestamp ?? null);
    const timestamps = (recent.data ?? []).map((r) => r.timestamp);
    setChartData(bucketEventsByDay(timestamps, CHART_DAYS));
    setLoading(false);
  }, []);

  // Initial fetch + debounced Realtime refresh on sentry_fingerprints.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    void fetchAll();
    const debouncedRefresh = (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => void fetchAll(), 1500);
    };
    const channel = supabase
      .channel('system-health-sentry')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sentry_fingerprints' },
        debouncedRefresh
      )
      .subscribe();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [fetchAll]);

  const sendTestEvent = useCallback((): void => {
    Sentry.captureException(new Error('[arbi-dev test] System Health pipeline check'));
    setToast({
      type: 'success',
      message: 'Test event sent to Sentry — it should appear in the Error Log Viewer and here shortly.',
    });
  }, []);

  const total7d = totalCount(chartData);

  return (
    <div className="h-full overflow-auto">
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <div className="max-w-5xl mx-auto p-5 space-y-4">
        {/* Header */}
        <div>
          <h1 className="text-base font-semibold text-ink-bright">System Health</h1>
          <p className="text-xs text-ink-muted">
            Sentry error tracking · {isProd ? 'production' : 'local dev'}
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Bug} label="Errors (24h)" value={fmtCount(errors24h)} tone="text-danger" />
          <StatCard
            icon={AlertTriangle}
            label="Errors (7d)"
            value={fmtCount(errors7d)}
            tone="text-warning"
          />
          <StatCard
            icon={Activity}
            label="Open issues"
            value={fmtCount(openIssues)}
            tone="text-accent"
          />
          <StatCard
            icon={Clock}
            label="Last event"
            value={lastEventAt ? relativeTime(lastEventAt) : '—'}
            tone="text-ink-secondary"
          />
        </div>

        {/* Error-rate-over-time chart */}
        <div className="arbi-card p-4">
          <h3 className="text-xs font-medium text-ink-secondary uppercase tracking-wider mb-3">
            Errors — last 7 days
          </h3>
          <div style={{ height: 240 }}>
            {loading ? (
              <div className="flex items-center justify-center h-full text-sm text-ink-muted">
                Loading…
              </div>
            ) : total7d > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={axisStyle}
                    axisLine={{ stroke: 'var(--border-subtle)' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={axisStyle}
                    axisLine={{ stroke: 'var(--border-subtle)' }}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--surface-3)' }} />
                  <Bar dataKey="count" name="Errors" fill="var(--accent)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <CheckCircle size={40} className="text-success mb-2" />
                <p className="text-sm text-ink-muted">No errors in the last 7 days</p>
              </div>
            )}
          </div>
        </div>

        {/* Diagnostics + deferred metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Pipeline-check (test event) */}
          <div className="arbi-card p-4">
            <span className="text-xs font-medium text-ink-secondary uppercase tracking-wider">
              Pipeline check
            </span>
            <p className="text-xs text-ink-muted mt-1 mb-3">
              Send a test error through Sentry to verify the pipeline end-to-end (Sentry → receiver →
              Error Log Viewer → this widget).
            </p>
            <button
              onClick={sendTestEvent}
              disabled={!isProd}
              className={clsx(
                'arbi-btn-ghost text-xs',
                !isProd && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Send size={12} /> Send test event
            </button>
            {!isProd && (
              <p className="text-2xs text-ink-muted mt-1.5">
                Disabled in local dev — Sentry transmits only on the deployed arbi-dev (Sentry
                init is prod-only).
              </p>
            )}
          </div>

          {/* Deferred metrics (TONE empty-state voice) */}
          <div className="arbi-card p-4 border-dashed">
            <div className="flex items-center gap-2">
              <Server size={14} className="text-ink-muted" />
              <span className="text-xs font-medium text-ink-secondary uppercase tracking-wider">
                More metrics coming
              </span>
            </div>
            <p className="text-xs text-ink-muted mt-1.5">
              Database query latency (p95), connection count, and Vercel build status are not wired
              yet — each needs a stats RPC or a platform-API connector. Sentry error tracking is
              live above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
